/**
 * LoanDraftSaver - Auto-save loan form data to temporary storage
 *
 * This module handles automatic saving of loan form data while the user is filling it out.
 * It prevents data loss if the user accidentally closes the page or loses the session.
 */

(function () {
  class LoanDraftSaver {
    constructor() {
      this.formSelector = 'form#formNewLoan';
      this.saveInterval = 3000; // Auto-save every 3 seconds
      this.autoSaveEnabled = true;
      this.lastSavedData = null;
      this.saveTimerId = null;
      this.slug = this.getSlug();
      this.isInitialized = false;
    }

    /**
     * Check if form exists in the DOM
     */
    formExists() {
      return document.querySelector(this.formSelector) !== null;
    }

    /**
     * Check if slug field exists and is valid
     */
    isSlugFieldValid() {
      const slugField = document.querySelector('#slug');
      return slugField !== null && slugField.value.trim() !== '';
    }

    /**
     * Initialize the draft saver
     */
    init() {
      // Only initialize if the form exists
      if (!this.formExists()) {
        console.log('[LoanDraftSaver] Formulario no encontrado. Auto-guardado deshabilitado.');
        return;
      }

      // Check if slug field exists with value
      if (!this.isSlugFieldValid()) {
        console.log('[LoanDraftSaver] Campo slug (#slug) no encontrado o vacío. Auto-guardado deshabilitado.');
        return;
      }

      this.attachEventListeners();
      this.startAutoSave();
      this.isInitialized = true;
      // console.log('[LoanDraftSaver] ✅ Inicializado con slug:', this.slug);
      // console.log('[LoanDraftSaver] ⏱️ Auto-guardado activado cada', this.saveInterval / 1000, 'segundos');
    }

    /**
     * Attach form change listeners
     */
    attachEventListeners() {
      const form = document.querySelector(this.formSelector);
      if (!form) {
        // console.log('[LoanDraftSaver] Formulario no encontrado. No se pueden adjuntar listeners.');
        return;
      }

      // Save on form input changes
      form.addEventListener('change', (e) => {
        this.debouncedSave();
      });

      // Save on input blur (when user leaves a field)
      form.addEventListener('blur', (e) => {
        if (e.target.matches('input, select, textarea')) {
          this.debouncedSave();
        }
      }, true);

      // Warn before leaving unsaved changes
      window.addEventListener('beforeunload', (e) => {
        if (this.hasUnsavedChanges()) {
          e.preventDefault();
          e.returnValue = '';
        }
      });

      // console.log('[LoanDraftSaver] Listeners adjuntados al formulario');
    }

    /**
     * Start auto-save timer
     */
    startAutoSave() {
      // console.log('[LoanDraftSaver] 🟢 Iniciando intervalo de auto-guardado cada', this.saveInterval / 1000, 'segundos');

      this.saveTimerId = setInterval(() => {
        // console.log('[LoanDraftSaver] ⏰ Intervalo ejecutándose...');
        // console.log('[LoanDraftSaver] Validando: enabled=', this.autoSaveEnabled, 'form=', this.formExists(), 'slug=', this.isSlugFieldValid());

        // Solo guardar si el módulo está habilitado, el formulario existe y el slug es válido
        if (this.autoSaveEnabled && this.formExists() && this.isSlugFieldValid()) {
          // console.log('[LoanDraftSaver] ✓ Todas las validaciones OK, ejecutando saveDraft()');
          this.saveDraft();
        } else if ((!this.formExists() || !this.isSlugFieldValid()) && this.autoSaveEnabled) {
          // console.log('[LoanDraftSaver] ❌ Formulario o slug no válido. Deteniendo auto-guardado.');
          this.stopAutoSave();
        }
      }, this.saveInterval);
    }

    /**
     * Stop auto-save timer
     */
    stopAutoSave() {
      if (this.saveTimerId) {
        clearInterval(this.saveTimerId);
        this.saveTimerId = null;
      }
    }

    /**
     * Debounced save function (avoid too many requests)
     */
    debouncedSave() {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.saveDraft();
      }, 2000); // Save 2 seconds after the user stops typing
    }

    /**
     * Get or create a unique slug for this form session
     * First checks if there's a slug field in the form (#slug)
     */
    getSlug() {
      // Primero intentar obtener del campo del formulario
      const slugField = document.querySelector('#slug');

      if (slugField && slugField.value) {
        // Si existe el campo y tiene valor, usar ese slug
        const fieldSlug = slugField.value;
        // console.log('[LoanDraftSaver] Slug encontrado en campo del formulario:', fieldSlug);
        sessionStorage.setItem('loanDraftSlug', fieldSlug);
        return fieldSlug;
      }

      const slugUrl = new URLSearchParams(window.location.search).get('slug');

      if (slugUrl) {
        // console.log('[LoanDraftSaver] Slug encontrado en URL:', slugUrl);
        sessionStorage.setItem('loanDraftSlug', slugUrl);
        return slugUrl;
      }

      // Si no hay slug en el campo, intentar obtener de sessionStorage
      let slug = sessionStorage.getItem('loanDraftSlug');
      return slug;
    }

    /**
     * Serialize form data
     */
    getFormData() {
      const form = document.querySelector(this.formSelector);
      if (!form) {
        // console.log('[LoanDraftSaver] ❌ Formulario no encontrado en getFormData()');
        return {};
      }

      const formData = new FormData(form);
      const data = {};

      for (let [key, value] of formData.entries()) {
        // Build nested object structure for Rails form fields
        if (key.includes('[')) {
          this.setNestedProperty(data, key, value);
        } else {
          data[key] = value;
        }
      }

      // console.log('[LoanDraftSaver] 📋 Datos del formulario capturados:', Object.keys(data));
      return data;
    }

    /**
     * Set nested property on object from Rails form field name
     * Example: "loan[customer_attributes][id_number]" -> data.loan.customer_attributes.id_number
     */
    setNestedProperty(obj, path, value) {
      const keys = path.match(/[^\[\]]+/g) || [];
      let current = obj;

      for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];
        if (!(key in current)) {
          current[key] = {};
        }
        current = current[key];
      }

      if (keys.length > 0) {
        current[keys[keys.length - 1]] = value;
      }
    }

    /**
     * Check if there are unsaved changes
     */
    hasUnsavedChanges() {
      const currentData = JSON.stringify(this.getFormData());
      return currentData !== this.lastSavedData;
    }

    /**
     * Save draft data via AJAX
     */
    saveDraft() {
      // Validar que el módulo está inicializado, el formulario existe y el slug es válido
      if (!this.autoSaveEnabled || !this.isInitialized || !this.formExists() || !this.isSlugFieldValid()) {
        // console.log('[LoanDraftSaver] ⚠️ Validación fallida en saveDraft():', {
        //   autoSaveEnabled: this.autoSaveEnabled,
        //   isInitialized: this.isInitialized,
        //   formExists: this.formExists(),
        //   isSlugFieldValid: this.isSlugFieldValid()
        // });
        return;
      }

      const loanData = this.getFormData();

      // Only save if data has changed
      const dataString = JSON.stringify(loanData);
      // console.log('[LoanDraftSaver] 📊 Comparando datos:', {
      //   hayDatos: dataString.length > 0,
      //   ultimo: this.lastSavedData?.substring(0, 50),
      //   actual: dataString.substring(0, 50),
      //   sonIguales: dataString === this.lastSavedData
      // });

      if (dataString === this.lastSavedData) {
        console.log('[LoanDraftSaver] ℹ️ Los datos no han cambiado, no se guarda');
        return;
      }

      // Extract loan data if it exists
      const loanParams = loanData.loan || loanData;

      const payload = {
        loan: loanParams,
        slug: this.slug,
        authenticity_token: this.getAuthToken()
      };

      console.log('[LoanDraftSaver] 📤 Enviando guardar con slug:', this.slug);

      fetch('/save_loan_draft', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.getAuthToken()
        },
        body: JSON.stringify(payload)
      })
        .then(response => response.json())
        .then(data => {
          if (data.success) {
            console.log('[LoanDraftSaver] ✅ Borrador guardado exitosamente', data);
            this.lastSavedData = dataString;

            // Store the slug if it was returned
            if (data.slug) {
              sessionStorage.setItem('loanDraftSlug', data.slug);
              this.slug = data.slug;
            }
          } else {
            console.error('[LoanDraftSaver] ❌ Error al guardar el borrador:', data.error);
          }
        })
        .catch(error => {
          console.error('[LoanDraftSaver] ❌ Error guardando borrador:', error);
        })
        .finally(() => {
        });
    }

    /**
     * Get CSRF token from meta tag
     */
    getAuthToken() {
      const token = document.querySelector('meta[name="csrf-token"]');
      return token ? token.getAttribute('content') : '';
    }

    /**
     * Disable auto-save temporarily
     */
    disable() {
      this.autoSaveEnabled = false;
      this.stopAutoSave();
    }

    /**
     * Re-enable auto-save
     */
    enable() {
      this.autoSaveEnabled = true;
      this.startAutoSave();
    }
  }

  // Export for use in other modules
  window.LoanDraftSaver = LoanDraftSaver;

  // Auto-initialize function
  function autoInitialize() {
    const form = document.querySelector('form#formNewLoan');
    const slugField = document.querySelector('#slug');

    // console.log('[LoanDraftSaver] 🔍 Buscando formulario y slug...', {
    //   formFound: !!form,
    //   slugFound: !!slugField,
    //   slugValue: slugField?.value?.substring(0, 10) + '...' || 'undefined'
    // });

    if (form && slugField && slugField.value.trim() !== '') {
      if (!window.loanDraftSaver) {
        const saver = new LoanDraftSaver();
        saver.init();
        window.loanDraftSaver = saver;
        // console.log('[LoanDraftSaver] ✨ Auto-inicializado exitosamente con slug del formulario');
      } else {
        // console.log('[LoanDraftSaver] ℹ️ Ya está inicializado');
      }
    } else {
      // console.log('[LoanDraftSaver] ⚠️ Formulario o campo slug (#slug) no encontrado/válido', {
      //   formFound: !!form,
      //   slugFound: !!slugField,
      //   slugEmpty: slugField?.value?.trim() === ''
      // });
    }
  }

  // Auto-initialize when DOM is ready
  if (document.readyState === 'loading') {
    // console.log('[LoanDraftSaver] ⏳ Esperando DOMContentLoaded...');
    document.addEventListener('DOMContentLoaded', autoInitialize);
  } else {
    // console.log('[LoanDraftSaver] ✅ DOM ya cargado, inicializando ahora...');
    autoInitialize();
  }
})();
